import { useMemo, useCallback, useEffect } from 'react';
import { Modal, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import Profile from '../../../../shared/components/Profile';
import type { DisplayGroup } from '../../../../shared/components/Profile/constants';
import Notification from '../../../../shared/components/Notification';
import { NotificationTypes } from '../../../../enums/notificationTypes';
import UserManagerService, { getUserManagerErrorMessage } from '../../../../services/UserManager/userManager.service';
import { userManagerServiceMessages } from '../../../../services/UserManager/userManager.constants';
import useMyProfileService from '../../../../services/MyProfileService/myProfile.service';
import { UserRole } from '../../../../enums/userEnums';
import type { Profile as ProfileModel, EditProfilePayload } from '../../../../models/profile.model';
import styles from './UserProfileModal.module.scss';

interface UserProfileModalProps {
  open: boolean;
  userId?: number;
  onClose: () => void;
  onEdit: (userId: number) => void;
}

const UserProfileModal = ({ open, userId, onClose, onEdit }: UserProfileModalProps) => {
  const { getUserDetails } = UserManagerService();
  const { getProfile } = useMyProfileService();

  const { data: userDetails, isLoading, error } = useQuery({
    ...getUserDetails(userId ?? 0),
    enabled: open && !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Subscription is org-level, not per-viewed-user -- the org profile query is the right source.
  const { data: orgProfileData } = useQuery({ ...getProfile(), enabled: open });

  const profile: ProfileModel | undefined = useMemo(
    () =>
      userDetails
        ? {
            id: userDetails.id,
            name: userDetails.name,
            title: userDetails.title,
            email: userDetails.email,
            phone: userDetails.phone ?? '',
            role: userDetails.role?.name,
            clientOrgName: userDetails.clientOrgName,
            subscriptionExpiresAt: userDetails.subscriptionExpiresAt,
          }
        : undefined,
    [
      userDetails?.id,
      userDetails?.name,
      userDetails?.title,
      userDetails?.email,
      userDetails?.phone,
      userDetails?.role?.name,
      userDetails?.clientOrgName,
      userDetails?.subscriptionExpiresAt,
    ],
  );

  // UserAccessRole carries no maps/files data.
  const groups: DisplayGroup[] = useMemo(
    () => (userDetails?.groups ?? []).map((group) => ({ id: String(group.id), name: group.name, maps: [], files: [] })),
    [userDetails?.groups],
  );

  const isViewedUserAdmin = userDetails?.role?.name === UserRole.ADMIN;

  const handleEditClick = useCallback(() => {
    if (userId) onEdit(userId);
  }, [userId, onEdit]);

  const noopSave = useCallback((_payload: EditProfilePayload, onDone: () => void) => onDone(), []);
  const noopSetPassword = useCallback((_newPassword: string, onSuccess: () => void) => onSuccess(), []);

  useEffect(() => {
    if (error) {
      Notification({
        message: userManagerServiceMessages.USERS.GET_DETAILS_ERROR,
        description: getUserManagerErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
      onClose();
    }
  }, [error, onClose]);

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={835} centered className={styles.userProfileModal}>
      {profile ? (
        <Profile
          profile={profile}
          onSave={noopSave}
          onEditClick={handleEditClick}
          isSaving={false}
          onSetPassword={noopSetPassword}
          onSetPasswordClick={handleEditClick}
          passwordIsSubmitting={false}
          groups={groups}
          canEditGroups={false}
          showSubscription={isViewedUserAdmin}
          subscription={orgProfileData?.subscription}
          renewalStatus={orgProfileData?.renewalStatus}
          subscriptionStatus={orgProfileData?.subscriptionStatus}
          showSubscriptionInfo={!isViewedUserAdmin}
        />
      ) : (
        <div className={styles.loadingState}>
          <Spin size="large" spinning={isLoading} />
        </div>
      )}
    </Modal>
  );
};

export default UserProfileModal;
